import { Box } from "@mui/material";
import { Link } from "react-router";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "./dataAtoms/shared";

/** A part of a whole, as a split bar draws it. */
export type SplitBarPart = {
  key: string;
  label: string;
  // the part as a fraction of the whole
  share: number;
  // how the share is written; as a percentage where left out
  shareText?: string;
  color: string;
  // the colour of the text on the part
  ink: string;
  /** Where the part leads to; a part without it is no link. */
  to?: string;
};

/**
 * A whole as one bar, split into its parts side by side. A part whose segment
 * is wide enough says what it is and what share it has; a narrower one keeps
 * its share, and one too narrow even for that says it only in its tooltip.
 */
export const SplitBar = ({ parts }: { parts: readonly SplitBarPart[] }) => {
  const { theme } = useTheme();
  const height = theme.spacing(4.25);

  return (
    <Box
      sx={{
        display: "flex",
        height,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: theme.palette.action.hover,
      }}
    >
      {parts.map(({ key, label, share, shareText, color, ink, to }) => {
        const figure = shareText ?? percentage.format(share);
        return (
          <Box
            key={key}
            title={`${label}: ${figure}`}
            {...(to ? { component: Link, to } : {})}
            sx={{
              width: `${share * 100}%`,
              bgcolor: color,
              color: ink,
              // what does not fit in the segment wraps onto lines below the
              // bar, out of sight: the share is laid first and the name after
              // it, behind an empty item that holds the visible line open, so
              // a narrow part keeps its share and a narrower one keeps nothing
              display: "flex",
              flexDirection: "row-reverse",
              flexWrap: "wrap",
              justifyContent: "center",
              alignContent: "flex-start",
              "&::before": { content: '""', height },
              typography: "caption",
              lineHeight: height,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              ...TABULAR_NUMERALS,
              ...(to && {
                textDecoration: "none",
                "&:hover": { opacity: 0.85 },
              }),
            }}
          >
            {/* laid after the share, but read before it */}
            <Box component="span" sx={{ order: 1, mr: 0.5 }}>
              {label}
            </Box>
            <span>{figure}</span>
          </Box>
        );
      })}
    </Box>
  );
};
